import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoList } from '@karozik/shared';
import { api } from '@karozik/ui';
import { preparePhoto, uploadPhoto } from './photoPrep';

export interface PhotoUpload {
  /** 1-based rank of the photo being sent, out of `total`. */
  rank: number;
  total: number;
  /** Upload progress of the current photo, 0 to 1. */
  progress: number;
}

export interface Photos {
  list: PhotoList | null;
  error: string | null;
  upload: PhotoUpload | null;
  add(files: File[]): void;
  remove(id: string): Promise<void>;
}

/**
 * The party's shared photos: the list, reloaded whenever the photo count in the
 * party state moves, and the guest's upload queue, one photo at a time (files
 * picked while it runs join the queue).
 */
export function usePhotos(joinToken: string, photoCount?: number): Photos {
  const base = `/api/join/${joinToken}/photos`;
  const [list, setList] = useState<PhotoList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upload, setUpload] = useState<PhotoUpload | null>(null);
  const queue = useRef<File[]>([]);
  const running = useRef(false);

  const reload = useCallback(() => {
    api<PhotoList>(base)
      .then(setList)
      .catch((e: Error) => setError(e.message));
  }, [base]);
  useEffect(reload, [reload, photoCount]);

  const run = async () => {
    if (running.current) return;
    running.current = true;
    let sent = 0;
    let failed = 0;
    let lastError = '';
    while (queue.current.length > 0) {
      const file = queue.current.shift()!;
      const rank = sent + failed + 1;
      setUpload({ rank, total: rank + queue.current.length, progress: 0 });
      try {
        const { photo, thumb } = await preparePhoto(file);
        const added = await uploadPhoto(base, photo, thumb, (progress) => setUpload((u) => u && { ...u, total: rank + queue.current.length, progress }));
        setList((l) => l && { ...l, photos: [added, ...l.photos.filter((p) => p.id !== added.id)] });
        sent++;
      } catch (e) {
        failed++;
        lastError = (e as Error).message;
      }
    }
    running.current = false;
    setUpload(null);
    if (failed === 1) setError(lastError);
    else if (failed > 1) setError(`${failed} photos n’ont pas pu être envoyées. ${lastError}`);
  };

  return {
    list,
    error,
    upload,
    add(files) {
      if (files.length === 0) return;
      queue.current.push(...files);
      setError(null);
      void run();
    },
    async remove(id) {
      await api(`${base}/${id}`, { method: 'DELETE' });
      setList((l) => l && { ...l, photos: l.photos.filter((p) => p.id !== id) });
    },
  };
}
